import { Card, CardContent, CardHeader, CardTitle } from "@exhousing/design-system";
import { Mail, Paperclip } from "lucide-react";
import { Link } from "react-router-dom";

import { StatusBadge } from "@/components/StatusBadge";
import {
  type InboxEntityType,
  useCorrespondence,
} from "@/hooks/api/useInbox";

/**
 * Correspondence timeline for one entity (contact / lead / contract), embedded on
 * that entity's detail page. Stays quiet when there's nothing to show.
 */
export function CorrespondencePanel({
  entityType,
  entityId,
  title = "Correspondence",
}: {
  entityType: InboxEntityType;
  entityId: string | undefined;
  title?: string;
}) {
  const { data: messages, isLoading } = useCorrespondence(entityType, entityId);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Mail className="size-4" />
          {title}
          {messages && messages.length > 0 && (
            <span className="text-sm font-normal text-muted-foreground">
              ({messages.length})
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : messages && messages.length > 0 ? (
          <ul className="space-y-1">
            {messages.map((m) => (
              <li key={m.id}>
                <Link
                  to={`/inbox/${m.id}`}
                  className="flex items-start gap-3 rounded-md px-2 py-2 transition-colors hover:bg-muted/50"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-medium">
                        {m.subject || "(no subject)"}
                      </span>
                      {m.has_attachments && (
                        <Paperclip className="size-3.5 shrink-0 text-muted-foreground" />
                      )}
                    </div>
                    <p className="truncate text-xs text-muted-foreground">
                      {m.from_name || m.from_addr || "—"}
                      {m.snippet ? ` — ${m.snippet}` : ""}
                    </p>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1">
                    <span className="text-xs text-muted-foreground">
                      {m.sent_at
                        ? new Date(m.sent_at).toLocaleDateString("nl-NL")
                        : ""}
                    </span>
                    {m.match_status === "multiple" && (
                      <StatusBadge status={m.match_status} />
                    )}
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">
            No correspondence linked yet.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
